import { remote, supertypeClass } from '@haventech/amorphic';
import { Policy } from '../../common/js/customer/Policy';
import { Serializable } from '@hit/amorphic-types';

import { WorkflowHelperInterface } from '../angular/interfaces/helper/WorkflowHelperInterface';
import { WorkflowStateType } from '../js/interfaces/CoverpathTypes';
import { HitAppCtx, HitAppExecutionCtx } from '../../common/js/orca/HitAppCtx';
import { WorkflowNames } from '../../common/js/orca/WorkflowName';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../common/js/logger/LoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class WorkflowHelper extends Serializable implements WorkflowHelperInterface {
  constructor() {
    super();
  }

  static new() {
    return new WorkflowHelper().init();
  }

  init() {
    return this;
  }

  @remote({ on: 'server' })
  publicRefresh(): Promise<void> {
    return Promise.resolve();
  }

  @remote({ on: 'server' })
  async getWorkflowStateAndSubStateFromPolicyNumber(policyNumber: string): Promise<WorkflowStateType> {
    return Policy.persistorFetchByQuery(
      { policyNumber: policyNumber },
      {
        fetch: { application: true },
        session: this.amorphic,
      }
    )
      .then((policies) => {
        if (policies && policies[0]) {
          return policies[0];
        }
        throw new Error(`policy for number ${policyNumber} not found`);
      })
      .then((policy) => {
        const result: WorkflowStateType = { state: null, subState: null };
        if (policy.futureWorkflowState) {
          result.state = policy.futureWorkflowState;
        } else {
          result.state = policy.workflowState;
        }
        if (policy.futureWorkflowSubState) {
          result.subState = policy.futureWorkflowSubState;
        } else {
          result.subState = policy.workflowSubState;
        }
        return result;
      })
      .catch((err) => {
        LoggerSingleton.getInstance().warn(
          {
            module: 'WorkflowHelper',
            function: 'getWorkflowStateAndSubStateFromPolicyNumber',
            category: Category.request,
          },
          err.message
        );
      });
  }

  @remote({ on: 'server' })
  async getWorkflowActivityResult(policy: Policy, activityName: string): Promise<string> {
    return HitAppCtx.get.startNewExecution(async (exnCtx) => {
      (exnCtx as HitAppExecutionCtx).workflowName = WorkflowNames.getDefaultWorkflow();

      const res = await HitAppCtx.get.policyClient.getActivityResult(policy.application, activityName);
      LoggerSingleton.getInstance().info(
        {
          module: 'WorkflowHelper',
          function: 'getWorkflowActivityResult',
          category: Category.request,
        },
        `Result for policy: ${policy.policyNumber} activity: ${activityName} is ${res}`
      );
      return res;
    }, HitAppCtx.newBaseMsgCtx(policy.customer._id));
  }

  /**
   * For a given policy's upcoming activity name and applicationId, return the scheduled activity execution time provided by Orca policy client.
   * For example: getScheduledActivityExecutionTime('TC2A5 Cancel TLIC No Signature', '5d07aac39d50210007fd2116', 'agent');
   */
  static async getScheduledActivityExecutionTime(
    activityName: string,
    applicationId: string,
    userId: string
  ): Promise<Date> {
    const activityCode = activityName.match(/^(\w+)/)[0];
    //For regression test cases, we will need to set currentTimeOffset in HitAppCtx.newMsgCtx.
    //We didn't setup here because this function is not tested in regression test.
    return HitAppCtx.get.startNewExecution((exnCtx) => {
      (exnCtx as HitAppExecutionCtx).workflowName = WorkflowNames.getDefaultWorkflow();
      return HitAppCtx.get.policyClient.getScheduledActivityExecution(applicationId, activityCode);
    }, HitAppCtx.newMsgCtx(userId, applicationId, 'ApplicationId', 'getExecutionTime'));
  }

  /**
   * For a given policy's applicationId, return current stage execution time provided by Orca policy client.
   * For example: getCurrentStageExecutionStage('5d07aac39d50210007fd2116', 'agent');
   */
  static async getCurrentStageExecutionStage(applicationId: string, userId: string): Promise<any> {
    //For regression test cases, we will need to set currentTimeOffset in HitAppCtx.newMsgCtx.
    //We didn't setup here because this function is not tested in regression test.
    return HitAppCtx.get.startNewExecution((exnCtx) => {
      (exnCtx as HitAppExecutionCtx).workflowName = WorkflowNames.getDefaultWorkflow();
      return HitAppCtx.get.policyClient.getCurrentStage(applicationId);
    }, HitAppCtx.newMsgCtx(userId, applicationId, 'ApplicationId', 'getExecutionTime'));
  }
}
